import * as Redux from 'redux'

import { RequestFailure } from '@owl-nest/api-client'
import { api } from '@owl-nest/api-client/latest'
import * as env from '@owl-nest/config'

import { internalError } from './messages/actions'

export type QueryAction<RETURN, STATE, BASIC_ACTION extends Redux.Action> = {
  type: 'query'
  query: Query<RETURN, STATE, BASIC_ACTION>
}

function isQueryAction(action: unknown): action is QueryAction<unknown, unknown, Redux.AnyAction> {
  return typeof action === 'object' && action !== null && 'type' in action && (action as any).type === 'query'
}

type BaseRequest = { type: string }
type BaseSuccess = { type: string; success: unknown }
type BaseFailure = { type: string; failure: RequestFailure & { bypassStatusAutomaticActions?: boolean } }

const KIND_PROP = '__queryStatus'

type QueryRequest<REQUEST extends BaseRequest = BaseRequest> = REQUEST & { [KIND_PROP]: 'request' }
type QuerySuccess<SUCCESS extends BaseSuccess> = SUCCESS & { [KIND_PROP]: 'success' }
type QueryFailure<FAILURE extends BaseFailure = BaseFailure> = FAILURE & { [KIND_PROP]: 'failure' }

type QueryBaseAction<
  REQUEST extends BaseRequest = BaseRequest,
  SUCCESS extends BaseSuccess = BaseSuccess,
  FAILURE extends BaseFailure = BaseFailure,
> = QueryRequest<REQUEST> | QuerySuccess<SUCCESS> | QueryFailure<FAILURE>

function isQueryBaseAction(action: unknown): action is QueryBaseAction {
  return typeof action === 'object' && action !== null && KIND_PROP in action
}

function isFailure(queryBaseAction: QueryBaseAction): queryBaseAction is QueryFailure {
  return queryBaseAction[KIND_PROP] === 'failure'
}

export type Query<RETURN, STATE, BASIC_ACTION extends Redux.Action> = (
  dispatch: {
    request: QueryDispatch<BASIC_ACTION & BaseRequest>
    success: QueryDispatch<BASIC_ACTION & BaseSuccess>
    failure: QueryDispatch<BASIC_ACTION & BaseFailure>
  },
  selector: <RESULT>(selector: (state: STATE) => RESULT) => RESULT,
) => RETURN

export interface QueryDispatch<BASIC_ACTION extends Redux.Action> {
  (action: BASIC_ACTION): BASIC_ACTION
}

export function createMiddleware(): Redux.Middleware<QueryDispatch<Redux.AnyAction>, QueryDispatch<Redux.AnyAction>> {
  return (store) => {
    const dispatch = {
      request: (action: BaseRequest) => store.dispatch({ ...action, [KIND_PROP]: 'request' }),
      success: (action: BaseSuccess) => store.dispatch({ ...action, [KIND_PROP]: 'success' }),
      failure: (action: BaseFailure) => store.dispatch({ ...action, [KIND_PROP]: 'failure' }),
    }
    return (next) => (action) => {
      if (isQueryAction(action)) {
        return action.query(dispatch, (selector) => selector(store.getState()))
      }

      if (isQueryBaseAction(action)) {
        if (isFailure(action)) {
          const failure = action.failure
          if (failure.status && failure.bypassStatusAutomaticActions !== true) {
            if (failure.status > 422 || failure.status === 405) {
              next(internalError())
            }
            if (failure.status === 403) {
              redirectToForbidden()
            }
            if (failure.status === 401 && failure.doNotRedirectToSignIn !== true) {
              redirectToSignin(failure.url)
            }
          }
        }
      }

      return next(action)
    }
  }

  function redirectToSignin(endpoint: string): void {
    const next = encodeURIComponent(window.location.href)
    const lang = env.getUserConfig().lang
    const doNotRedirectEndpoints = [api.endpoints.me.url]

    if (doNotRedirectEndpoints.includes(endpoint)) {
      return
    }

    const redirectUrl = env.SIGNIN_URL + `?next=${next}&lang=${lang}`
    window.location.href = redirectUrl
  }

  function redirectToForbidden(): void {
    const next = encodeURIComponent(window.location.href)
    const lang = env.getUserConfig().lang

    window.location.href = env.FORBIDDEN_URL + `?next=${next}&lang=${lang}`
  }
}

declare module 'redux' {
  /*
   * Overload to add promise support to Redux's dispatch() function.
   * Useful for react-redux or any other library which could use this type.
   */
  export interface Dispatch<A extends Redux.Action = Redux.UnknownAction> {
    <STATE, RETURN = unknown>(thunkAction: Query<RETURN, STATE, A>): RETURN
  }
}
